import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User } from './../models/user';
import { Species } from '../models/species';
import { Disease } from '../models/disease';
import { SurgicalOperation } from '../models/surgicalOperation';
import { Organization, OrganizationMember, OrganizationFosterFamily, OrganizationSubscription, OrganizationSubscriptionTransaction } from './../models/organization';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { UserService } from './user.service';
import { DateUtilsService } from './date-utils.service';
import { DiseaseService } from './disease.service';
import { SurgicalOperationService } from './surgical-operation.service';
import { IndexedDbService } from './indexed-db.service';
import { blobToBase64 } from '../utils'
import { TranslateService } from '@ngx-translate/core';
import { Litter } from '../models/litter';
import { LitterService } from './litter.service';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  organizations: Organization[] = [];
  roles: { [key: string]: any }[] = [];

  constructor(
    private http: HttpClient,
    public storageService: StorageService,
    public networkService: NetworkService,
    private translate: TranslateService,
    private dateUtils: DateUtilsService,
    private userService: UserService,
    public indexedDbService: IndexedDbService,
    public alertController: AlertController) { }

  dbSaveOrganization(organization: Organization): Promise<Organization> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organization.id;
        formData["name"] = organization.name;
        formData["isActivePublicPage"] = organization.isActivePublicPage;
        formData["publicDescriptionHTML"] = organization.publicDescriptionHTML;
        formData["publicDescriptionJSON"] = organization.publicDescriptionJSON;
        formData["email"] = organization.email;
        formData["phoneNumber"] = organization.phoneNumber;
        formData["addrStreet"] = organization.addrStreet;
        formData["addrCity"] = organization.addrCity;
        formData["addrPostalCode"] = organization.addrPostalCode;
        formData["addrCountry"] = organization.addrCountry;
        formData["postDefaultVisiblity"] = organization.postDefaultVisiblity;
        formData["postDefaultVisiblityLitter"] = organization.postDefaultVisiblityLitter;
        formData["affix"] = organization.affix;
        
        if(organization.avatarNewFileName != null) {
          formData["avatar"] = {'base64': organization.avatarBase64, 'name': organization.avatarNewFileName}
        }
        
        let url = environment.API_URL + 'organization/ws_private/UpdateOrganization/'
        if(organization.id == null)
          url = environment.API_URL + 'organization/ws_public/CreateOrganization/'

        this.http.post<any[]>(url, formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (organization.id == null) {
              organization.id = response['id']
            }
            resolve(organization);
          },
          error: async (error) => {
            resolve(organization)
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': organization, 'action': 'save'}).subscribe(() => {});
        resolve(organization)
      }
    });
  }

  dbSaveOrganizationMember(organizationId: number, organizationMember: OrganizationMember) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationMember.id;

        formData["userId"] = organizationMember.userId;
        formData["email"] = organizationMember.email;
        formData["firstName"] = organizationMember.firstName;
        formData["lastName"] = organizationMember.lastName;
        formData["isActive"] = organizationMember.isActive;
        formData["role"] = organizationMember.role;

        formData["isRoleAdmin"] = organizationMember.isRoleAdmin;
        formData["isRoleWriteMembers"] = organizationMember.isRoleWriteMembers;
        formData["isRoleReadMembers"] = organizationMember.isRoleReadMembers;
        formData["isRoleWriteAnimals"] = organizationMember.isRoleWriteAnimals;
        formData["isRoleReadAnimals"] = organizationMember.isRoleReadAnimals;
        formData["isRoleWriteAgenda"] = organizationMember.isRoleWriteAgenda;
        formData["isRoleReadAgenda"] = organizationMember.isRoleReadAgenda;
        formData["isRoleWritePlan"] = organizationMember.isRoleWritePlan;
        formData["isRoleReadPlan"] = organizationMember.isRoleReadPlan;

        formData["isRoleWritePharmacy"] = organizationMember.isRoleWritePharmacy;
        formData["isRoleReadPharmacy"] = organizationMember.isRoleReadPharmacy;
        formData["isRoleWriteShare"] = organizationMember.isRoleWriteShare;
        formData["isRoleReadShare"] = organizationMember.isRoleReadShare;
        formData["isRoleWriteVet"] = organizationMember.isRoleWriteVet;
        formData["isRoleReadVet"] = organizationMember.isRoleReadVet;
        formData["isRoleWriteFosterFamily"] = organizationMember.isRoleWriteFosterFamily;
        formData["isRoleReadFosterFamily"] = organizationMember.isRoleReadFosterFamily;
        formData["isRoleWriteOrganization"] = organizationMember.isRoleWriteOrganization;
        formData["isBenevoleWalker"] = organizationMember.isBenevoleWalker;

        formData["isRoleWriteCompta"] = organizationMember.isRoleWriteCompta;
        formData["isRoleReadCompta"] = organizationMember.isRoleReadCompta;

        formData["isRoleDiscussionModerator"] = organizationMember.isRoleDiscussionModerator;

        this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/UpdateMember/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (organizationMember.id == null) {
              organizationMember.id = response['id']
            }
            resolve(organizationMember);
          },
          error: async (error) => {
            if (error.status === 400 && error.error.error == 'EmailAlreadyUsed') {
              var alert = await this.alertController.create({
                header: this.translate.instant("Warning"),
                subHeader: this.translate.instant("Unable to save"),
                message: this.translate.instant("Un membre de l'équipe utilise déjà cette adresse email, vous ne pouvez pas en créer un second."),
                buttons: ['OK'],
              });
              await alert.present();
              reject(error);
            } else {
              var alert = await this.alertController.create({
                header: this.translate.instant("Warning"),
                subHeader: this.translate.instant("Unable to save"),
                message: this.translate.instant("An error occurred, the backup failed."),
                buttons: ['OK'],
              });
              await alert.present();
              reject(error);
            }
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': organizationMember, 'action': 'save'}).subscribe(() => {});
        resolve(organizationMember)
      }
    });
  }

  dbGetOrganization(litterService: LitterService, organizationId: number | null): Promise<Organization | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationId;

        var url = environment.API_URL + 'organization/ws_public/' + organizationId + '/GetOrganization/';
        if(organizationId == null)
          url = environment.API_URL + 'organization/ws_public/GetOrganizationBlank/';

        this.http.post<any[]>(url, formData, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var organization = this.organizations.find(a => a.id == organizationId)
          if(!organization){
            organization = new Organization()
            organization.id = response['organization']['id']
            this.organizations.push(organization);
          }
          organization.name = response['organization']['name']
          organization.types = response['organization']['types']
          organization.isActivePublicPage = response['organization']['isActivePublicPage']
          organization.publicDescriptionHTML = response['organization']['publicDescriptionHTML']
          organization.publicDescriptionJSON = response['organization']['publicDescriptionJSON']
          organization.avatar = response['organization']['avatar']
          organization.userId = response['organization']['userId']

          organization.email = response['organization']['email']
          organization.phoneNumber = response['organization']['phoneNumber']
          organization.addrStreet = response['organization']['addrStreet']
          organization.addrCity = response['organization']['addrCity']
          organization.addrPostalCode = response['organization']['addrPostalCode']
          organization.addrCountry = response['organization']['addrCountry']
          organization.postDefaultVisiblity = response['organization']['postDefaultVisiblity']
          organization.postDefaultVisiblityLitter = response['organization']['postDefaultVisiblityLitter']
          organization.affix = response['organization']['affix']

          if(response['organization']['litters'] != null && litterService) {
            organization.litters = litterService.convertHttpResponsesLitters(response['organization'])
          }

          if(response['organization']['avatarBase64'] != null) {
            organization.avatarBase64 = response['organization']['avatarBase64'];
            resolve(organization);
          } else if(organization.avatar != null) {
            organization.avatarBase64 = null;
            this.http.get(organization.avatar,  { responseType: 'blob' as 'blob' }).subscribe(data => {
              blobToBase64(data).then((res: any) =>  {
                if(organization)
                  organization.avatarBase64 = res;
                resolve(organization ?? null);
              });
            });
          } else {
            resolve(organization);
          }

          resolve(organization);
        },
        () => {
          resolve(this.organizations.find(a => a.id == organizationId) ?? null);
        })
      } else {
        resolve(this.organizations.find(a => a.id ==organizationId) ?? null);
      }
    });
  }


  dbGetStatsOfOrganization(organizationId: number, year: number, month: number): Promise<{ [key: string]: any }> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationId;
        formData["month"] = month;
        formData["year"] = year;

        var url = environment.API_URL + 'organization/ws_private/' + organizationId + '/GetStatsOfOrganization/';

        this.http.post<any[]>(url, formData, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          resolve(response);
        },
        () => {
          resolve({});
        })
      } else {
        resolve({});
      }
    });
  }


  dbGetOrganizationMember(organizationId: number, organizationMemberId: number | null): Promise<OrganizationMember | null | undefined> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationMemberId;

        var url = environment.API_URL + 'organization/ws_private/' + organizationId + '/GetMember/';
        if(organizationMemberId == null)
          url = environment.API_URL + 'organization/ws_private/GetMemberBlank/';

        this.http.post<any[]>(url, formData, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var organization = this.organizations.find(a => a.id == response['organization']['id'])
          if(!organization){
            organization = new Organization()
            organization.id = response['organization']['id']
            organization.name = response['organization']['name']
            organization.types = response['organization']['types']
            organization.avatar = response['organization']['avatar']
            organization.userId = response['organization']['userId']
  
            if(response['avatarBase64'] != null) {
              organization.avatarBase64 = response['avatarBase64'];
            } else if(organization.avatar != null) {
              organization.avatarBase64 = null;
              this.http.get(response['avatar'],  { responseType: 'blob' as 'blob' }).subscribe(data => {
                blobToBase64(data).then((res: any) =>  {
                  if(organization)
                    organization.avatarBase64 = res;
                });
              });
            }
            
            this.organizations.push(organization);
          }

          this.roles = response['roles']

          var organizationMember = organization.members.find(a => a.id == response['member']['id'])
          if(!organizationMember){
            organizationMember = new OrganizationMember()
            organization.members.push(organizationMember);
          }

          organizationMember.id = response['member']['id']
          organizationMember.userId = response['member']['userId']
          organizationMember.email = response['member']['email']
          organizationMember.isActive = response['member']['isActive']
          organizationMember.role = response['member']['role']

          organizationMember.firstName = response['member']['firstName']
          organizationMember.lastName = response['member']['lastName']
          organizationMember.isRoleAdmin = response['member']['isRoleAdmin']
          organizationMember.isRoleWriteMembers = response['member']['isRoleWriteMembers']
          organizationMember.isRoleReadMembers = response['member']['isRoleReadMembers']
          organizationMember.isRoleWriteAnimals = response['member']['isRoleWriteAnimals']
          organizationMember.isRoleReadAnimals = response['member']['isRoleReadAnimals']
          organizationMember.isRoleWriteAgenda = response['member']['isRoleWriteAgenda']
          organizationMember.isRoleReadAgenda = response['member']['isRoleReadAgenda']
          organizationMember.isRoleWritePlan = response['member']['isRoleWritePlan']
          organizationMember.isRoleReadPlan = response['member']['isRoleReadPlan']

          organizationMember.isRoleWritePharmacy = response['member']['isRoleWritePharmacy']
          organizationMember.isRoleReadPharmacy = response['member']['isRoleReadPharmacy']
          organizationMember.isRoleWriteShare = response['member']['isRoleWriteShare']
          organizationMember.isRoleReadShare = response['member']['isRoleReadShare']
          organizationMember.isRoleWriteFosterFamily = response['member']['isRoleWriteFosterFamily']
          organizationMember.isRoleReadFosterFamily = response['member']['isRoleReadFosterFamily']
          organizationMember.isRoleWriteVet = response['member']['isRoleWriteVet']
          organizationMember.isRoleReadVet = response['member']['isRoleReadVet']
          organizationMember.isRoleWriteOrganization = response['member']['isRoleWriteOrganization']
          organizationMember.isBenevoleWalker = response['member']['isBenevoleWalker']

          organizationMember.isRoleWriteCompta = response['member']['isRoleWriteCompta']
          organizationMember.isRoleReadCompta = response['member']['isRoleReadCompta']
          organizationMember.isRoleDiscussionModerator = response['member']['isRoleDiscussionModerator']

          resolve(organizationMember);
        },
        () => {
          resolve( (this.organizations.find(a => a.id == organizationId) ?? new Organization()).members.find(a => a.id == organizationMemberId) );
        })
      } else {
        resolve( (this.organizations.find(a => a.id == organizationId) ?? new Organization()).members.find(a => a.id == organizationMemberId) );
      }
    });
  }

  dbGetMembersOfOrganization(organizationId: number): Promise<OrganizationMember[] | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/GetMembersOfOrganization/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          
          var organization = this.organizations.find(a => a.id == response['organization']['id'])
          if(!organization){
            organization = new Organization()
            organization.id = response['organization']['id']
            organization.name = response['organization']['name']
            organization.types = response['organization']['types']
            organization.avatar = response['organization']['avatar']
            organization.userId = response['organization']['userId']
  
            if(response['avatarBase64'] != null) {
              organization.avatarBase64 = response['avatarBase64'];
            } else if(organization.avatar != null && response['avatar'] != null) {
              organization.avatarBase64 = null;
              this.http.get(response['avatar'],  { responseType: 'blob' as 'blob' }).subscribe(data => {
                blobToBase64(data).then((res: any) =>  {
                  if(organization)
                    organization.avatarBase64 = res;
                });
              });
            }
            this.organizations.push(organization);
          }

          for(let m of response['members']) {
            var organizationMember = organization.members.find(a => a.id == m['id'])
            if(!organizationMember){
              organizationMember = new OrganizationMember()
              organization.members.push(organizationMember);
            }

            organizationMember.id = m['id']
            organizationMember.userId = m['userId']
            organizationMember.email = m['email']
            organizationMember.isActive = m['isActive']
            organizationMember.firstName = m['firstName']
            organizationMember.lastName = m['lastName']
            organizationMember.role = m['role']

            organizationMember.isRoleAdmin = m['isRoleAdmin']
            organizationMember.isRoleWriteMembers = m['isRoleWriteMembers']
            organizationMember.isRoleReadMembers = m['isRoleReadMembers']
            organizationMember.isRoleWriteAnimals = m['isRoleWriteAnimals']
            organizationMember.isRoleReadAnimals = m['isRoleReadAnimals']
            organizationMember.isRoleWriteAgenda = m['isRoleWriteAgenda']
            organizationMember.isRoleReadAgenda = m['isRoleReadAgenda']
            organizationMember.isRoleWritePlan = m['isRoleWritePlan']
            organizationMember.isRoleReadPlan = m['isRoleReadPlan']

            organizationMember.isRoleWritePharmacy = m['isRoleWritePharmacy']
            organizationMember.isRoleReadPharmacy = m['isRoleReadPharmacy']
            organizationMember.isRoleWriteShare = m['isRoleWriteShare']
            organizationMember.isRoleReadShare = m['isRoleReadShare']
            organizationMember.isRoleWriteFosterFamily = m['isRoleWriteFosterFamily']
            organizationMember.isRoleReadFosterFamily = m['isRoleReadFosterFamily']
            organizationMember.isRoleWriteVet = m['isRoleWriteVet']
            organizationMember.isRoleReadVet = m['isRoleReadVet']
            organizationMember.isRoleWriteOrganization = m['isRoleWriteOrganization']
            organizationMember.isBenevoleWalker = m['isBenevoleWalker']
            organizationMember.isRoleWriteCompta = m['isRoleWriteCompta']
            organizationMember.isRoleReadCompta = m['isRoleReadCompta']
            organizationMember.isRoleDiscussionModerator = m['isRoleDiscussionModerator']
          }
          resolve(organization.members);
        },
        () => {
          resolve((this.organizations.find(a => a.id == organizationId) ?? new Organization()).members);
        })
      } else {
        resolve((this.organizations.find(a => a.id == organizationId) ?? new Organization()).members);
      }
    });
  }

  dbDeleteOrganizationMember(organizationId: number, organizationMember: OrganizationMember) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationMember.id;

        var organization = this.organizations.find(a => a.id == organizationId)
        if(organization) {
          organization.members = organization.members.filter(a => a.id != organizationMember.id);
        
          this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/DeleteMember/', formData, {headers : headers}).subscribe({
            next: (response: { [key: string]: any }) => {
              resolve(organizationMember);
            },
            error: async (error) => {
              reject(error);
            }
          });
        }
      } else {
        this.indexedDbService.addItem({'obj': organizationMember, 'action': 'delete'}).subscribe(() => {});
        resolve(organizationMember)
      }
    });
  }

  resendEmailActivationMember(organizationId: number, organizationMember: OrganizationMember) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationMember.id;
        this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/resendEmailActivationMember/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(organizationMember);
          },
          error: async (error) => {
            reject(error);
          }
        });
      }
    });
  }


  dbGetOrganizationFosterFamily(organizationId: number, organizationFosterFamilyId: number | null): Promise<OrganizationFosterFamily | null | undefined> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationFosterFamilyId;

        var url = environment.API_URL + 'organization/ws_private/' + organizationId + '/GetFosterFamily/';
        if(organizationFosterFamilyId == null)
          url = environment.API_URL + 'organization/ws_private/GetFosterFamilyBlank/';

        this.http.post<any[]>(url, formData, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var organization = this.organizations.find(a => a.id == response['organization']['id'])
          if(!organization){
            organization = new Organization()
            organization.id = response['organization']['id']
            organization.name = response['organization']['name']
            organization.types = response['organization']['types']
            this.organizations.push(organization);
          }

          var fosterFamily = organization.fosterFamilies.find(a => a.id == response['fosterFamily']['id'])
          if(!fosterFamily){
            fosterFamily = new OrganizationFosterFamily()
            organization.fosterFamilies.push(fosterFamily);
          }

          fosterFamily.id = response['fosterFamily']['id']
          fosterFamily.firstName = response['fosterFamily']['firstName']
          fosterFamily.lastName = response['fosterFamily']['lastName']
          fosterFamily.phoneNumber = response['fosterFamily']['phoneNumber']
          fosterFamily.address = response['fosterFamily']['address']
          fosterFamily.email = response['fosterFamily']['email']
          
          resolve(fosterFamily);
        },
        () => {
          resolve( (this.organizations.find(a => a.id == organizationId) ?? new Organization()).fosterFamilies.find(a => a.id == organizationFosterFamilyId) );
        })
      } else {
        resolve( (this.organizations.find(a => a.id == organizationId) ?? new Organization()).fosterFamilies.find(a => a.id == organizationFosterFamilyId) );
      }
    });
  }

  dbGetFosterFamiliesOfOrganization(organizationId: number): Promise<OrganizationFosterFamily[] | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/GetFosterFamiliesOfOrganization/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          
          var organization = this.organizations.find(a => a.id == response['organization']['id'])
          if(!organization){
            organization = new Organization()
            organization.id = response['organization']['id']
            organization.name = response['organization']['name']
            organization.types = response['organization']['types']
            this.organizations.push(organization);
          }

          for(let m of response['fosterFamilies']) {
            var organizationFosterFamily = organization.fosterFamilies.find(a => a.id == m['id'])
            if(!organizationFosterFamily){
              organizationFosterFamily = new OrganizationFosterFamily()
              organization.fosterFamilies.push(organizationFosterFamily);
            }

            organizationFosterFamily.id = m['id']
            organizationFosterFamily.firstName = m['firstName']
            organizationFosterFamily.lastName = m['lastName']
            organizationFosterFamily.phoneNumber = m['phoneNumber']
            organizationFosterFamily.address = m['address']
            organizationFosterFamily.email = m['email']
          }
          resolve(organization.fosterFamilies);
        },
        () => {
          resolve((this.organizations.find(a => a.id == organizationId) ?? new Organization()).fosterFamilies);
        })
      } else {
        resolve((this.organizations.find(a => a.id == organizationId) ?? new Organization()).fosterFamilies);
      }
    });
  }

  dbDeleteOrganizationFosterFamily(organizationId: number, organizationFosterFamily: OrganizationFosterFamily) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationFosterFamily.id;

        var organization = this.organizations.find(a => a.id == organizationId)
        if(organization) {
          organization.fosterFamilies = organization.fosterFamilies.filter(a => a.id != organizationFosterFamily.id);
        
          this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/DeleteFosterFamily/', formData, {headers : headers}).subscribe({
            next: (response: { [key: string]: any }) => {
              resolve(organizationFosterFamily);
            },
            error: async (error) => {
              reject(error);
            }
          });
        }
      } else {
        this.indexedDbService.addItem({'obj': organizationFosterFamily, 'action': 'delete'}).subscribe(() => {});
        resolve(organizationFosterFamily)
      }
    });
  }

  dbSaveOrganizationFosterFamily(organizationId: number, organizationFosterFamily: OrganizationFosterFamily) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = organizationFosterFamily.id;

        formData["firstName"] = organizationFosterFamily.firstName;
        formData["lastName"] = organizationFosterFamily.lastName;
        formData["address"] = organizationFosterFamily.address;
        formData["phoneNumber"] = organizationFosterFamily.phoneNumber;
        formData["email"] = organizationFosterFamily.email;
        console.log(formData)
        
        this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/UpdateFosterFamily/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (organizationFosterFamily.id == null) {
              organizationFosterFamily.id = response['id']
            }
            resolve(organizationFosterFamily);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': organizationFosterFamily, 'action': 'save'}).subscribe(() => {});
        resolve(organizationFosterFamily)
      }
    });
  }


  dbGetSubscriptionsAvailable(organization: Organization): Promise<OrganizationSubscription[]> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
        });
  
        this.http.get<any[]>(environment.API_URL + 'organization/ws_private/' + organization.id + '/GetSubscriptionsAvailable/', {headers : headers}).subscribe({
          next: async (response: { [key: string]: any }) => {
            organization.subscription = response['subscriptions'].find((a: { [key: string]: any }) => a['id'] == response['currentSubscription']) ?? null;
            resolve(response['subscriptions']);
        },
        error: async (error) => {
          resolve([]);
        }
      });
      } else {
        resolve([]);
      }
    });
  }

  dbSaveOrganizationSubscriptionTransaction(organizationId: number, organizationSubscriptionTransaction: OrganizationSubscriptionTransaction) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
        });

        let formData: { [key: string]: any } = {};
        formData["id"] = organizationSubscriptionTransaction.id;
        formData["userId"] = organizationSubscriptionTransaction.userId;
        formData["subscription"] = organizationSubscriptionTransaction.subscription;
        formData["transactionId"] = organizationSubscriptionTransaction.transactionId;
        formData["transactionSource"] = organizationSubscriptionTransaction.transactionSource;
        formData["receipt"] = organizationSubscriptionTransaction.receipt;

        this.http.post<any[]>(environment.API_URL + 'organization/ws_private/' + organizationId + '/SaveOrganizationSubscriptionTransaction/', organizationSubscriptionTransaction, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(organizationSubscriptionTransaction);
          },
          error: async (error) => {
            reject(error);
          }
        });
      }
    });
  }

}
